import { useState, useEffect, useRef } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import {
  Shield, Crosshair, Zap, AlertTriangle, Target, Activity, Clock, CheckCircle,
  XCircle, Loader2, Play, Square, ChevronRight, FileText, Bug, Lock, Globe,
  Code, Server, Wifi, Database, RefreshCw, Download, Eye, Brain, Calendar,
  Trash2, Power, Edit, Plus, X, ExternalLink, Upload, FileSpreadsheet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { AnimatedBackground } from '@/components/AnimatedBackground';

const SCAN_TYPES = [
  { value: 'vulnerability_scan', label: 'Vulnerability Scan', icon: Bug, description: 'Comprehensive vulnerability assessment' },
  { value: 'penetration_test', label: 'Penetration Test', icon: Crosshair, description: 'Simulated attack testing' },
  { value: 'web_app_scan', label: 'Web Application', icon: Globe, description: 'Web app security testing' },
  { value: 'api_security', label: 'API Security', icon: Code, description: 'API endpoint security testing' },
  { value: 'network_recon', label: 'Network Recon', icon: Wifi, description: 'Network reconnaissance' },
  { value: 'full', label: 'Full Enterprise Scan', icon: Shield, description: 'Complete infrastructure assessment' },
  { value: 'cloud_config', label: 'Cloud Configuration', icon: Server, description: 'AWS/Azure/GCP security audit' },
  { value: 'compliance', label: 'Compliance Scan', icon: CheckCircle, description: 'Regulatory compliance checks' },
];

const FREQUENCIES = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

// Target validation patterns by scan type
const TARGET_VALIDATION = {
  vulnerability_scan: {
    pattern: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$|^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/,
    placeholder: 'Enter URL, IP address, or CIDR (e.g., https://example.com, 192.168.1.0/24)',
    hint: 'Accepts web URLs, IP addresses, or CIDR ranges',
    examples: ['https://example.com', '192.168.1.1', '10.0.0.0/24']
  },
  penetration_test: {
    pattern: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$|^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/,
    placeholder: 'Enter target URL or IP range for penetration testing',
    hint: 'Accepts web URLs, IP addresses, or network ranges',
    examples: ['https://target.com', '192.168.1.1', '10.0.0.0/24']
  },
  web_app_scan: {
    pattern: /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/,
    placeholder: 'Enter web application URL (must start with http:// or https://)',
    hint: 'Web application URL required (e.g., https://app.example.com)',
    examples: ['https://app.example.com', 'http://localhost:8080/app']
  },
  api_security: {
    pattern: /^https?:\/\/.+\.(json|yaml|yml)$|^https?:\/\/.*\/?(swagger|openapi|api-docs|graphql)/i,
    placeholder: 'Enter API spec URL (OpenAPI/Swagger JSON/YAML) or API base URL',
    hint: 'Accepts OpenAPI spec URLs (.json/.yaml) or API endpoints (/api, /graphql)',
    examples: ['https://api.example.com/swagger.json', 'https://api.example.com/v1']
  },
  network_recon: {
    pattern: /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$|^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
    placeholder: 'Enter IP address, CIDR range, or hostname',
    hint: 'Accepts IP addresses, CIDR ranges, or hostnames for network reconnaissance',
    examples: ['192.168.1.0/24', '10.0.0.1', 'server.example.com']
  },
  full: {
    pattern: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$|^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/,
    placeholder: 'Enter target domain, URL, or IP range for full enterprise scan',
    hint: 'Comprehensive scan accepts any target type',
    examples: ['https://enterprise.com', '192.168.1.0/24', 'target.example.com']
  },
  cloud_config: {
    pattern: /^(aws|azure|gcp):\/\/|^(arn:aws:|https:\/\/.*\.amazonaws\.com|https:\/\/.*\.azure\.com|https:\/\/.*\.googleapis\.com)/,
    placeholder: 'Enter cloud resource ARN, URL, or identifier (aws://account-id, arn:aws:...)',
    hint: 'Accepts AWS ARNs, Azure resource IDs, or GCP project URLs',
    examples: ['arn:aws:s3:::bucket-name', 'https://storage.googleapis.com/bucket']
  },
  compliance: {
    pattern: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$|^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/,
    placeholder: 'Enter target URL or IP for compliance assessment',
    hint: 'Accepts web URLs or IP addresses for compliance scanning',
    examples: ['https://compliance-target.com', '10.0.0.1']
  }
};

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const SEVERITY_COLORS = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-blue-500',
  info: 'bg-slate-400'
};

export default function RaptorModule() {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('scan');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Scan State
  const [scanType, setScanType] = useState('vulnerability_scan');
  const [target, setTarget] = useState('');
  const [targetError, setTargetError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [currentScan, setCurrentScan] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  
  // Assessment Metadata
  const [assessmentName, setAssessmentName] = useState('');
  const [assessmentTags, setAssessmentTags] = useState('');
  
  // False Positives State
  const [reportSubTab, setReportSubTab] = useState('findings'); // 'findings' or 'false_positives'
  
  // Playbook State
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [interactiveMode, setInteractiveMode] = useState(false);
  const [interactiveNotes, setInteractiveNotes] = useState([]);
  
  // Authentication State for Enterprise Scans
  const [authEnabled, setAuthEnabled] = useState(false);
  const [authType, setAuthType] = useState('basic');
  const [authCredentials, setAuthCredentials] = useState({
    username: '',
    password: '',
    token: '',
    oauth_client_id: '',
    oauth_client_secret: '',
    oauth_token_url: ''
  });
  
  // Scheduled Scans State
  const [scheduledScans, setScheduledScans] = useState([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    scanType: 'vulnerability_scan',
    target: '',
    frequency: 'daily',
    hour: 2,
    minute: 0,
    dayOfWeek: 0,
    dayOfMonth: 1
  });
  const [creatingSchedule, setCreatingSchedule] = useState(false);
  
  // Secure Code Analysis State
  const [codeFile, setCodeFile] = useState(null);
  const [codeAssessmentName, setCodeAssessmentName] = useState('');
  const [codeTags, setCodeTags] = useState('');
  const [uploadingCode, setUploadingCode] = useState(false);
  const [codeScans, setCodeScans] = useState([]);
  const codeFileRef = useRef(null);
  
  useEffect(() => {
    fetchStatus();
    fetchScanHistory();
    fetchScheduledScans();
    fetchCodeScans();
  }, []);
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let interval;
    if (currentScan && ['initializing', 'running'].includes(currentScan.status)) {
      interval = setInterval(() => {
        fetchScanStatus(currentScan.id);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [currentScan]);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/raptor/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch SecureProbe status');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchScanHistory = async () => {
    try {
      const response = await api.get('/raptor/scans?limit=20');
      setScanHistory(response.data.scans || []);
    } catch (error) {
      console.error('Failed to fetch scan history');
    }
  };
  
  const fetchScheduledScans = async () => {
    try {
      const response = await api.get('/raptor/scheduled');
      setScheduledScans(response.data.schedules || []);
    } catch (error) {
      console.error('Failed to fetch scheduled scans');
    }
  };
  
  const fetchCodeScans = async () => {
    try {
      const response = await api.get('/secure-code/scans');
      setCodeScans(response.data.scans || []);
    } catch (error) {
      console.error('Failed to fetch code scans');
    }
  };
  
  const handleCodeUpload = async () => {
    if (!codeFile) {
      toast.error('Please select a code archive to upload');
      return;
    }
    
    if (!codeAssessmentName.trim()) {
      toast.error('Assessment Name is required for code analysis');
      return;
    }
    
    setUploadingCode(true);
    try {
      const formData = new FormData();
      formData.append('file', codeFile);
      formData.append('assessment_name', codeAssessmentName.trim());
      formData.append('tags', codeTags.trim());
      
      const response = await api.post('/secure-code/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Code scan completed: ${response.data.total_vulnerabilities || 0} issues found`);
      setCodeFile(null);
      setCodeAssessmentName('');
      setCodeTags('');
      if (codeFileRef.current) codeFileRef.current.value = '';
      fetchCodeScans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan code');
    } finally {
      setUploadingCode(false);
    }
  };
  
  const fetchNotes = async () => {
    try {
      const response = await api.get('/raptor/notes');
      setInteractiveNotes(response.data.notes || []);
    } catch (error) {
      console.error('Failed to fetch notes');
    }
  };
  
  // Fetch notes on mount
  useEffect(() => {
    fetchNotes();
  }, []);
  
  const createScheduledScan = async () => {
    if (!scheduleForm.target.trim()) {
      toast.error('Please enter a target');
      return;
    }
    
    setCreatingSchedule(true);
    try {
      const scheduleConfig = {
        frequency: scheduleForm.frequency,
        hour: parseInt(scheduleForm.hour),
        minute: parseInt(scheduleForm.minute),
        day_of_week: parseInt(scheduleForm.dayOfWeek),
        day_of_month: parseInt(scheduleForm.dayOfMonth)
      };
      
      await api.post('/raptor/scheduled', {
        name: scheduleForm.name || `Scheduled ${scheduleForm.scanType.replace('_', ' ')}`,
        scan_type: scheduleForm.scanType,
        target: scheduleForm.target.trim(),
        schedule_config: scheduleConfig
      });
      
      toast.success('Scheduled scan created');
      setShowScheduleForm(false);
      setScheduleForm({
        name: '',
        scanType: 'vulnerability_scan',
        target: '',
        frequency: 'daily',
        hour: 2,
        minute: 0,
        dayOfWeek: 0,
        dayOfMonth: 1
      });
      fetchScheduledScans();
    } catch (error) {
      toast.error('Failed to create scheduled scan');
    } finally {
      setCreatingSchedule(false);
    }
  };
  
  const toggleScheduledScan = async (scheduleId, isActive) => {
    try {
      await api.post(`/raptor/scheduled/${scheduleId}/toggle?is_active=${!isActive}`);
      toast.success(isActive ? 'Scheduled scan disabled' : 'Scheduled scan enabled');
      fetchScheduledScans();
    } catch (error) {
      toast.error('Failed to toggle scheduled scan');
    }
  };
  
  const deleteScheduledScan = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled scan?')) return;
    
    try {
      await api.delete(`/raptor/scheduled/${scheduleId}`);
      toast.success('Scheduled scan deleted');
      fetchScheduledScans();
    } catch (error) {
      toast.error('Failed to delete scheduled scan');
    }
  };
  
  const runScheduledScanNow = async (scheduleId) => {
    try {
      const response = await api.post(`/raptor/scheduled/${scheduleId}/run`);
      toast.success('Scheduled scan triggered');
      setCurrentScan({ id: response.data.scan_id, status: 'initializing', progress: 0 });
      setActiveTab('results');
      fetchScheduledScans();
    } catch (error) {
      toast.error('Failed to trigger scheduled scan');
    }
  };
  
  // Target validation function
  const validateTarget = (value, type) => {
    if (!value.trim()) {
      return { valid: false, error: 'Target is required' };
    }
    
    const validation = TARGET_VALIDATION[type];
    if (!validation) {
      return { valid: true, error: '' };
    }
    
    // For cloud_config, allow more flexible input
    if (type === 'cloud_config') {
      // Accept any cloud-related URL or identifier
      if (value.includes('aws') || value.includes('azure') || value.includes('gcp') || 
          value.includes('amazonaws') || value.includes('googleapis') || value.includes('arn:')) {
        return { valid: true, error: '' };
      }
      // Also accept regular URLs for cloud-hosted apps
      if (/^https?:\/\//.test(value)) {
        return { valid: true, error: '' };
      }
      return { 
        valid: false, 
        error: `Invalid cloud resource. ${validation.hint}. Examples: ${validation.examples.join(', ')}` 
      };
    }
    
    // For API security, also accept any URL with /api in path
    if (type === 'api_security') {
      if (/^https?:\/\/.+/.test(value) && (
        value.includes('/api') || value.includes('swagger') || value.includes('openapi') || 
        value.includes('graphql') || value.endsWith('.json') || value.endsWith('.yaml') || value.endsWith('.yml')
      )) {
        return { valid: true, error: '' };
      }
      // Also accept any valid URL as API base
      if (/^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}/.test(value)) {
        return { valid: true, error: '' };
      }
    }
    
    // For web_app_scan, require http/https
    if (type === 'web_app_scan') {
      if (!/^https?:\/\//.test(value)) {
        return { 
          valid: false, 
          error: 'Web application URL must start with http:// or https://' 
        };
      }
    }
    
    // General URL/IP validation for other types
    const isValidUrl = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/.test(value);
    const isValidIp = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/.test(value);
    const isValidHostname = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*$/.test(value);
    
    if (isValidUrl || isValidIp || isValidHostname) {
      return { valid: true, error: '' };
    }
    
    return { 
      valid: false, 
      error: `Invalid target format. ${validation.hint}` 
    };
  };
  
  // Handle target change with validation
  const handleTargetChange = (value) => {
    setTarget(value);
    if (value.trim()) {
      const result = validateTarget(value, scanType);
      setTargetError(result.valid ? '' : result.error);
    } else {
      setTargetError('');
    }
  };
  
  // Re-validate when scan type changes
  useEffect(() => {
    if (target.trim()) {
      const result = validateTarget(target, scanType);
      setTargetError(result.valid ? '' : result.error);
    }
  }, [scanType]);
  
  const fetchScanStatus = async (scanId) => {
    try {
      const response = await api.get(`/raptor/scan/${scanId}`);
      setCurrentScan(response.data);
      if (response.data.status === 'completed') {
        fetchScanHistory();
        toast.success('Scan completed successfully');
      } else if (response.data.status === 'failed') {
        toast.error('Scan failed: ' + (response.data.error || 'Unknown error'));
      } else if (response.data.status === 'running' || response.data.status === 'initializing') {
        // Continue polling every 2 seconds
        setTimeout(() => fetchScanStatus(scanId), 2000);
      }
    } catch (error) {
      console.error('Failed to fetch scan status');
    }
  };
  
  const startScan = async () => {
    if (!target.trim()) {
      toast.error('Please enter a target');
      return;
    }
    
    // Validate Assessment Name is provided
    if (!assessmentName.trim()) {
      toast.error('Assessment Name is required');
      return;
    }
    
    // Validate target based on scan type
    const validation = validateTarget(target, scanType);
    if (!validation.valid) {
      toast.error(validation.error);
      setTargetError(validation.error);
      return;
    }
    
    setScanning(true);
    try {
      const payload = {
        scan_type: scanType,
        target: target.trim(),
        assessment_name: assessmentName.trim(),
        tags: assessmentTags.trim() ? assessmentTags.split(',').map(t => t.trim()).filter(t => t) : [],
        options: {
          playbook: selectedPlaybook,
          interactive_mode: interactiveMode
        }
      };
      
      // Add authentication if enabled
      if (authEnabled) {
        payload.authentication = {
          enabled: true,
          type: authType,
          credentials: authType === 'basic' ? {
            username: authCredentials.username,
            password: authCredentials.password
          } : authType === 'bearer' ? {
            token: authCredentials.token
          } : {
            client_id: authCredentials.oauth_client_id,
            client_secret: authCredentials.oauth_client_secret,
            token_url: authCredentials.oauth_token_url
          }
        };
      }
      
      const response = await api.post('/raptor/scan', payload);
      
      setCurrentScan({ id: response.data.scan_id, status: 'initializing', progress: 0 });
      toast.success('Scan started: ' + response.data.message);
      setActiveTab('results');
      
      // Start polling for status
      fetchScanStatus(response.data.scan_id);
    } catch (error) {
      toast.error('Failed to start scan');
    } finally {
      setScanning(false);
    }
  };
  
  const cancelScan = async () => {
    if (!currentScan) return;
    
    try {
      await api.post(`/raptor/scan/${currentScan.id}/cancel`);
      toast.info('Scan cancelled');
      setCurrentScan(null);
      fetchScanHistory();
    } catch (error) {
      toast.error('Failed to cancel scan');
    }
  };
  
  const viewScanDetails = (scan) => {
    setSelectedScan(scan);
    setActiveTab('details');
  };
  
  const retestScan = async (scan) => {
    setTarget(scan.target);
    setScanType(scan.scan_type || 'vulnerability_scan');
    setActiveTab('scan');
    toast.info('Target loaded from previous scan. Click "Start SecureProbe" to begin retest.');
  };
  
  // Mark finding as false positive
  const markAsFalsePositive = async (findingIndex) => {
    if (!selectedScan) return;
    
    try {
      const findings = [...selectedScan.findings];
      const finding = findings[findingIndex];
      finding.is_false_positive = true;
      finding.false_positive_marked_at = new Date().toISOString();
      
      await api.put(`/raptor/scan/${selectedScan.id}/findings`, { findings });
      
      // Update local state
      setSelectedScan({ ...selectedScan, findings });
      
      // Update scan history
      setScanHistory(prev => prev.map(s => 
        s.id === selectedScan.id ? { ...s, findings } : s
      ));
      
      toast.success('Finding marked as false positive');
    } catch (error) {
      toast.error('Failed to mark as false positive');
    }
  };
  
  // Restore finding from false positives
  const restoreFromFalsePositive = async (findingIndex) => {
    if (!selectedScan) return;
    
    try {
      const findings = [...selectedScan.findings];
      const falsePositives = findings.filter(f => f.is_false_positive);
      const finding = falsePositives[findingIndex];
      
      // Find the original index and update
      const originalIndex = findings.findIndex(f => f === finding);
      findings[originalIndex] = { ...finding, is_false_positive: false };
      
      await api.put(`/raptor/scan/${selectedScan.id}/findings`, { findings });
      
      setSelectedScan({ ...selectedScan, findings });
      setScanHistory(prev => prev.map(s => 
        s.id === selectedScan.id ? { ...s, findings } : s
      ));
      
      toast.success('Finding restored from false positives');
    } catch (error) {
      toast.error('Failed to restore finding');
    }
  };
  
  // Export Code Analysis Report (PDF or HTML)
  const exportCodeAnalysisReport = async (scanId, format = 'pdf') => {
    try {
      toast.info(`Generating ${format.toUpperCase()} report...`);
      
      const response = await api.get(`/secure-code/scan/${scanId}/export?format=${format}`, {
        responseType: format === 'pdf' ? 'blob' : 'text'
      });
      
      if (format === 'pdf') {
        // For PDF, open HTML in new window and prompt print
        const htmlContent = response.data;
        const newWindow = window.open('', '_blank');
        newWindow.document.write(htmlContent);
        newWindow.document.close();
        toast.info('Use Ctrl+P / Cmd+P to save as PDF');
      } else {
        // For HTML, download the file
        const blob = new Blob([response.data], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `secure-code-report-${scanId}.html`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Report downloaded');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export ${format.toUpperCase()} report`);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6 relative">
      <AnimatedBackground variant="cyber" />
      
      {/* Header */}
      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${isDarkMode ? 'from-red-600/30 to-orange-600/30' : 'from-red-100 to-orange-100'}`}>
            <Crosshair className={`w-6 h-6 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
          </div>
          <div>
            <h1 className={`text-2xl font-rajdhani font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              SecureProbe
            </h1>
            <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
              Autonomous Offensive/Defensive Research Framework
            </p>
          </div>
        </div>
        
        {/* Status Badge */}
        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${status?.enabled ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'}`}>
          {status?.enabled ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          <span className="text-sm font-medium">
            {status?.enabled ? 'AI Engine Active' : 'AI Engine Offline'}
          </span>
        </div>
      </div>
      
      {/* Capabilities */}
      {status?.enabled && (
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 relative z-10`}>
          {status?.capabilities?.slice(0, 8).map((cap, idx) => (
            <div 
              key={idx}
              className={`px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-2 ${
                isDarkMode ? 'bg-zinc-800/50 text-zinc-300' : 'bg-slate-100 text-slate-700'
              }`}
            >
              <Zap className="w-3 h-3 text-indigo-500" />
              {cap.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </div>
          ))}
        </div>
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="relative z-10">
        <TabsList className={`${isDarkMode ? 'bg-zinc-900/50' : 'bg-slate-100'}`}>
          <TabsTrigger value="scan" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            <Crosshair className="w-4 h-4 mr-2" />
            New Scan
          </TabsTrigger>
          <TabsTrigger value="code-analysis" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            <Code className="w-4 h-4 mr-2" />
            Code Analysis
          </TabsTrigger>
          <TabsTrigger value="playbooks" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />
            Playbooks
          </TabsTrigger>
          <TabsTrigger value="scheduled" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            <Calendar className="w-4 h-4 mr-2" />
            Scheduled
            {scheduledScans.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] bg-white/20">
                {scheduledScans.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="results" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            <Activity className="w-4 h-4 mr-2" />
            Active Scan
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            <Clock className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
          <TabsTrigger value="notes" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            <Edit className="w-4 h-4 mr-2" />
            Notes
          </TabsTrigger>
          <TabsTrigger value="details" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            <Eye className="w-4 h-4 mr-2" />
            Report
          </TabsTrigger>
        </TabsList>
        
        {/* New Scan Tab */}
        <TabsContent value="scan" className="space-y-4">
          <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Configure Security Scan
            </h3>
            
            {/* Scan Type Selection */}
            <div className="mb-6">
              <Label className={`text-sm mb-3 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                Select Scan Type
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                {SCAN_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setScanType(type.value)}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        scanType === type.value
                          ? isDarkMode 
                            ? 'bg-red-600/20 border-red-500/50 ring-2 ring-red-500/30' 
                            : 'bg-red-50 border-red-300 ring-2 ring-red-200'
                          : isDarkMode 
                            ? 'bg-zinc-800/50 border-white/10 hover:border-white/20' 
                            : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mb-2 ${scanType === type.value ? 'text-red-500' : isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`} />
                      <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{type.label}</p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{type.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Target Input */}
            <div className="mb-6">
              <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                Target
              </Label>
              <Input
                data-testid="raptor-target-input"
                value={target}
                onChange={(e) => handleTargetChange(e.target.value)}
                placeholder={TARGET_VALIDATION[scanType]?.placeholder || 'Enter target URL, IP, or domain'}
                className={`${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300'} ${
                  targetError ? 'border-red-500 ring-1 ring-red-500' : ''
                }`}
              />
              {targetError ? (
                <p className="text-xs mt-2 text-red-500 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {targetError}
                </p>
              ) : (
                <div className="mt-2 space-y-1">
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    {TARGET_VALIDATION[scanType]?.hint || 'Ensure you have authorization to scan this target.'}
                  </p>
                  {TARGET_VALIDATION[scanType]?.examples && (
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`}>
                      Examples: {TARGET_VALIDATION[scanType].examples.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* Assessment Name and Tags */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  Assessment Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  data-testid="raptor-assessment-name"
                  value={assessmentName}
                  onChange={(e) => setAssessmentName(e.target.value)}
                  placeholder="e.g., Q1 Security Audit"
                  className={`${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300'} ${!assessmentName.trim() ? 'border-orange-500/50' : ''}`}
                  required
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Required - identifies this scan in reports and filters
                </p>
              </div>
              <div>
                <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  Tags
                </Label>
                <Input
                  data-testid="raptor-tags-input"
                  value={assessmentTags}
                  onChange={(e) => setAssessmentTags(e.target.value)}
                  placeholder="e.g., production, web-tier, pci-scope"
                  className={`${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300'}`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Comma-separated tags for categorization
                </p>
              </div>
            </div>
            
            {/* Authentication Section */}
            <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-800/50 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <Label className={`text-sm font-medium flex items-center gap-2 ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  <Lock className="w-4 h-4" />
                  Authenticated Scan
                </Label>
                <Switch
                  checked={authEnabled}
                  onCheckedChange={setAuthEnabled}
                  data-testid="raptor-auth-toggle"
                />
              </div>
              
              {authEnabled && (
                <div className="space-y-3">
                  <Select value={authType} onValueChange={setAuthType}>
                    <SelectTrigger className={`text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : ''}`}>
                      <SelectValue placeholder="Select auth type" />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                      <SelectItem value="basic">Basic Auth (Username/Password)</SelectItem>
                      <SelectItem value="bearer">Bearer Token</SelectItem>
                      <SelectItem value="oauth">OAuth 2.0</SelectItem>
                    </SelectContent>
                  </Select>

                  {authType === 'basic' && (
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder="Username"
                        value={authCredentials.username}
                        onChange={(e) => setAuthCredentials({...authCredentials, username: e.target.value})}
                        className={`text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : ''}`}
                        data-testid="raptor-auth-username"
                      />
                      <Input
                        type="password"
                        placeholder="Password"
                        value={authCredentials.password}
                        onChange={(e) => setAuthCredentials({...authCredentials, password: e.target.value})}
                        className={`text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : ''}`}
                        data-testid="raptor-auth-password"
                      />
                    </div>
                  )}

                  {authType === 'bearer' && (
                    <Input
                      placeholder="Bearer Token"
                      value={authCredentials.token}
                      onChange={(e) => setAuthCredentials({...authCredentials, token: e.target.value})}
                      className={`text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : ''}`}
                      data-testid="raptor-auth-token"
                    />
                  )}

                  {authType === 'oauth' && (
                    <div className="space-y-2">
                      <Input
                        placeholder="Client ID"
                        value={authCredentials.oauth_client_id}
                        onChange={(e) => setAuthCredentials({...authCredentials, oauth_client_id: e.target.value})}
                        className={`text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : ''}`}
                      />
                      <Input
                        type="password"
                        placeholder="Client Secret"
                        value={authCredentials.oauth_client_secret}
                        onChange={(e) => setAuthCredentials({...authCredentials, oauth_client_secret: e.target.value})}
                        className={`text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : ''}`}
                      />
                      <Input
                        placeholder="Token URL"
                        value={authCredentials.oauth_token_url}
                        onChange={(e) => setAuthCredentials({...authCredentials, oauth_token_url: e.target.value})}
                        className={`text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : ''}`}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Selected Playbook Indicator */}
            {selectedPlaybook && (
              <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-red-500" />
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Playbook: {selectedPlaybook.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Mapped to scan type: {
                          {
                            web_recon: 'Web Application Scan',
                            api_pentest: 'API Security',
                            network_pentest: 'Network Recon',
                            owasp_top10: 'Web Application Scan',
                            red_team_lite: 'Penetration Test',
                            cloud_security: 'Cloud Configuration'
                          }[selectedPlaybook] || scanType.replace('_', ' ')
                        }
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedPlaybook(null)}
                    className="text-red-500 hover:text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            
            {/* Start Scan Button */}
            <Button
              data-testid="raptor-start-scan-btn"
              onClick={startScan}
              disabled={scanning || !target.trim() || !!targetError || !status?.enabled}
              className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white"
              size="lg"
            >
              {scanning ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Play className="w-5 h-5 mr-2" />
              )}
              {selectedPlaybook ? `Start ${selectedPlaybook.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Scan` : 'Start SecureProbe'}
            </Button>
          </div>
        </TabsContent>
        
        {/* Secure Code Analysis Tab */}
        <TabsContent value="code-analysis" className="space-y-4">
          <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-emerald-600/20 border border-emerald-500/30' : 'bg-emerald-100 border border-emerald-200'}`}>
                <Code className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
              </div>
              <div>
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Secure Code Analysis</h3>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Upload code archives for AST-based static security analysis
                </p>
              </div>
            </div>
            
            <div className={`p-4 rounded-lg border mb-4 ${isDarkMode ? 'bg-zinc-800/50 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
              <p className={`text-sm mb-3 ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                Enterprise-grade code analysis engine scans for:
              </p>
              <ul className={`grid grid-cols-1 md:grid-cols-3 gap-2 text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                <li className="flex items-center gap-2"><Bug className="w-3 h-3 text-red-500" /> Hardcoded Secrets & API Keys</li>
                <li className="flex items-center gap-2"><Bug className="w-3 h-3 text-red-500" /> SQL Injection Vulnerabilities</li>
                <li className="flex items-center gap-2"><Bug className="w-3 h-3 text-red-500" /> Insecure Deserialization</li>
                <li className="flex items-center gap-2"><Bug className="w-3 h-3 text-orange-500" /> Cross-Site Scripting (XSS)</li>
                <li className="flex items-center gap-2"><Bug className="w-3 h-3 text-orange-500" /> Command Injection</li>
                <li className="flex items-center gap-2"><Bug className="w-3 h-3 text-orange-500" /> JWT Security Issues</li>
                <li className="flex items-center gap-2"><Bug className="w-3 h-3 text-yellow-500" /> Weak Cryptography</li>
                <li className="flex items-center gap-2"><Bug className="w-3 h-3 text-yellow-500" /> Path Traversal</li>
                <li className="flex items-center gap-2"><Bug className="w-3 h-3 text-yellow-500" /> SSL/TLS Misconfigurations</li>
              </ul>
              <p className={`text-xs mt-3 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                ✓ Vulnerabilities are automatically pushed to Vulnerability Hub for unified tracking
              </p>
            </div>
            
            {/* Assessment Name and Tags for Code Analysis */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <Label className={`text-xs mb-1.5 block ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                  Assessment Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={codeAssessmentName}
                  onChange={(e) => setCodeAssessmentName(e.target.value)}
                  placeholder="e.g., Frontend Code Review Q1"
                  data-testid="code-assessment-name"
                  className={`text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'} ${!codeAssessmentName.trim() ? 'border-orange-500/50' : ''}`}
                  required
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Required - identifies scan in Vulnerability Hub
                </p>
              </div>
              <div>
                <Label className={`text-xs mb-1.5 block ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                  Tags
                </Label>
                <Input
                  value={codeTags}
                  onChange={(e) => setCodeTags(e.target.value)}
                  placeholder="e.g., frontend, react, pci-scope"
                  data-testid="code-tags"
                  className={`text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Comma-separated tags for categorization
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
              <div className="flex-1">
                <Label className={`text-xs mb-1.5 block ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                  Code Archive (ZIP, TAR, TAR.GZ)
                </Label>
                <Input
                  ref={codeFileRef}
                  type="file"
                  accept=".zip,.tar,.tar.gz,.tgz"
                  onChange={(e) => setCodeFile(e.target.files?.[0] || null)}
                  data-testid="code-file-input"
                  className={`text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white file:text-white file:bg-zinc-800 file:border-0' : 'bg-white border-slate-300 file:bg-slate-100 file:border-0'}`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Maximum file size: 50MB • Supports Python, JS, TS, Java, PHP, Go, C/C++
                </p>
              </div>
              <Button
                onClick={handleCodeUpload}
                disabled={!codeFile || !codeAssessmentName.trim() || uploadingCode}
                data-testid="upload-code-btn"
                className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
              >
                {uploadingCode ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload & Scan
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {/* Code Scan History */}
          <div className={`rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className={`p-4 border-b ${isDarkMode ? 'border-white/5' : 'border-slate-200'}`}>
              <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Code Scan History</h4>
            </div>
            {codeScans.length > 0 ? (
              <div className="divide-y divide-white/10">
                {codeScans.map((scan, idx) => (
                  <div key={idx} className={`p-4 ${isDarkMode ? 'hover:bg-zinc-800/50' : 'hover:bg-slate-50'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {scan.assessment_name || scan.filename}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {scan.filename} • {scan.files_scanned} files • {new Date(scan.scanned_at).toLocaleDateString()}
                        </p>
                        {scan.tags && scan.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {scan.tags.map((tag, i) => (
                              <span key={i} className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-600'}`}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          {scan.severity_breakdown?.critical > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-500">
                              {scan.severity_breakdown.critical} Crit
                            </span>
                          )}
                          {scan.severity_breakdown?.high > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-orange-500/20 text-orange-500">
                              {scan.severity_breakdown.high} High
                            </span>
                          )}
                          {scan.severity_breakdown?.medium > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-500">
                              {scan.severity_breakdown.medium} Med
                            </span>
                          )}
                        </div>
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                          {scan.total_vulnerabilities} issues
                        </span>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => exportCodeAnalysisReport(scan.id, 'pdf')}
                            className={`text-xs ${isDarkMode ? 'text-indigo-400 hover:bg-indigo-500/10' : 'text-indigo-600 hover:bg-indigo-50'}`}
                            data-testid={`export-code-pdf-${idx}`}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            PDF
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => exportCodeAnalysisReport(scan.id, 'html')}
                            className={`text-xs ${isDarkMode ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-emerald-600 hover:bg-emerald-50'}`}
                            data-testid={`export-code-html-${idx}`}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            HTML
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Code className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                <p className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>No Code Scans Yet</p>
                <p className={`text-sm mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Upload a code archive to analyze for security vulnerabilities
                </p>
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* Scheduled Scans Tab */}
        <TabsContent value="scheduled" className="space-y-4">
          {/* Create Schedule Button */}
          <div className="flex justify-end">
            <Button
              onClick={() => setShowScheduleForm(!showScheduleForm)}
              className="bg-indigo-600 hover:bg-indigo-500 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Schedule
            </Button>
          </div>
          
          {/* Schedule Form */}
          {showScheduleForm && (
            <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Create Scheduled Scan
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Schedule Name</Label>
                  <Input
                    value={scheduleForm.name}
                    onChange={(e) => setScheduleForm({...scheduleForm, name: e.target.value})}
                    placeholder="e.g., Weekly Production Scan"
                    className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300'}`}
                  />
                </div>
                <div>
                  <Label className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Target</Label>
                  <Input
                    value={scheduleForm.target}
                    onChange={(e) => setScheduleForm({...scheduleForm, target: e.target.value})}
                    placeholder="https://example.com"
                    className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300'}`}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <Label className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Scan Type</Label>
                  <Select value={scheduleForm.scanType} onValueChange={(v) => setScheduleForm({...scheduleForm, scanType: v})}>
                    <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : 'bg-white'}>
                      {SCAN_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Frequency</Label>
                  <Select value={scheduleForm.frequency} onValueChange={(v) => setScheduleForm({...scheduleForm, frequency: v})}>
                    <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : 'bg-white'}>
                      {FREQUENCIES.map(freq => (
                        <SelectItem key={freq.value} value={freq.value}>{freq.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Hour (0-23)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="23"
                    value={scheduleForm.hour}
                    onChange={(e) => setScheduleForm({...scheduleForm, hour: e.target.value})}
                    className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300'}`}
                  />
                </div>
                <div>
                  <Label className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Minute</Label>
                  <Input
                    type="number"
                    min="0"
                    max="59"
                    value={scheduleForm.minute}
                    onChange={(e) => setScheduleForm({...scheduleForm, minute: e.target.value})}
                    className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300'}`}
                  />
                </div>
              </div>
              
              {scheduleForm.frequency === 'weekly' && (
                <div className="mb-4">
                  <Label className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Day of Week</Label>
                  <Select value={String(scheduleForm.dayOfWeek)} onValueChange={(v) => setScheduleForm({...scheduleForm, dayOfWeek: parseInt(v)})}>
                    <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : 'bg-white'}>
                      {DAYS_OF_WEEK.map(day => (
                        <SelectItem key={day.value} value={String(day.value)}>{day.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {scheduleForm.frequency === 'monthly' && (
                <div className="mb-4">
                  <Label className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Day of Month (1-28)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="28"
                    value={scheduleForm.dayOfMonth}
                    onChange={(e) => setScheduleForm({...scheduleForm, dayOfMonth: e.target.value})}
                    className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300'}`}
                  />
                </div>
              )}
              
              <div className="flex gap-3">
                <Button
                  onClick={createScheduledScan}
                  disabled={creatingSchedule || !scheduleForm.target.trim()}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                >
                  {creatingSchedule ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Create Schedule
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowScheduleForm(false)}
                  className={isDarkMode ? 'border-white/10' : ''}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          
          {/* Scheduled Scans List */}
          <div className={`rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            {scheduledScans.length > 0 ? (
              <div className="divide-y divide-white/10">
                {scheduledScans.map((schedule, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 ${isDarkMode ? 'hover:bg-zinc-800/50' : 'hover:bg-slate-50'} transition-colors`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${schedule.is_active ? 'bg-emerald-500/20' : 'bg-zinc-500/20'}`}>
                          <Calendar className={`w-5 h-5 ${schedule.is_active ? 'text-emerald-500' : isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              {schedule.name}
                            </p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              schedule.is_active 
                                ? 'bg-emerald-500/20 text-emerald-500' 
                                : isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-slate-200 text-slate-600'
                            }`}>
                              {schedule.is_active ? 'Active' : 'Paused'}
                            </span>
                          </div>
                          <p className={`text-xs font-mono mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            {schedule.target}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                              {schedule.scan_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                            <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                              {schedule.schedule_config?.frequency}
                              {schedule.schedule_config?.frequency !== 'hourly' && ` @ ${String(schedule.schedule_config?.hour || 0).padStart(2, '0')}:${String(schedule.schedule_config?.minute || 0).padStart(2, '0')}`}
                            </span>
                            {schedule.run_count > 0 && (
                              <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                                {schedule.run_count} runs
                              </span>
                            )}
                          </div>
                          {schedule.next_run_at && (
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                              Next: {new Date(schedule.next_run_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => runScheduledScanNow(schedule.id)}
                          className={isDarkMode ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'}
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleScheduledScan(schedule.id, schedule.is_active)}
                          className={schedule.is_active ? 'text-yellow-500 hover:text-yellow-400' : 'text-emerald-500 hover:text-emerald-400'}
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteScheduledScan(schedule.id)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Calendar className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                <p className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>No Scheduled Scans</p>
                <p className={`text-sm mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Create a scheduled scan to automate security testing
                </p>
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* Active Scan Tab */}
        <TabsContent value="results" className="space-y-4">
          {currentScan ? (
            <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {currentScan.status === 'running' ? (
                    <Loader2 className="w-5 h-5 animate-spin text-red-500" />
                  ) : currentScan.status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  ) : currentScan.status === 'failed' ? (
                    <XCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <Clock className="w-5 h-5 text-yellow-500" />
                  )}
                  <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Scan {currentScan.status === 'running' ? 'In Progress' : currentScan.status}
                  </h3>
                </div>
                {['initializing', 'running'].includes(currentScan.status) && (
                  <Button variant="destructive" size="sm" onClick={cancelScan}>
                    <Square className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                )}
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className={isDarkMode ? 'text-zinc-400' : 'text-slate-600'}>Progress</span>
                  <span className={isDarkMode ? 'text-zinc-400' : 'text-slate-600'}>{currentScan.progress || 0}%</span>
                </div>
                <Progress value={currentScan.progress || 0} className="h-2" />
                
                {/* Current Activity Display */}
                {currentScan.current_activity && (
                  <div className={`mt-3 p-3 rounded-lg flex items-center gap-3 ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                    <Loader2 className="w-4 h-4 animate-spin text-red-500 flex-shrink-0" />
                    <div>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {currentScan.current_activity}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {currentScan.status === 'running' ? 'Scan in progress...' : currentScan.status}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {currentScan.target && (
                <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Target</p>
                  <p className={`font-mono text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{currentScan.target}</p>
                </div>
              )}
              
              {/* Playbook Phase Tracking */}
              {currentScan.playbook_name && currentScan.playbook_phases && (
                <div className={`mb-4 p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-800/30 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-red-500" />
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {currentScan.playbook_name}
                      </span>
                    </div>
                    <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      Phase {(currentScan.current_phase_index || 0) + 1} of {currentScan.playbook_phases.length}
                    </span>
                  </div>
                  
                  {/* Phase Progress Steps */}
                  <div className="space-y-2">
                    {currentScan.playbook_phases.map((phase, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center gap-3 p-2 rounded-lg transition-all ${
                          phase.status === 'running' 
                            ? isDarkMode ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'
                            : phase.status === 'completed'
                            ? isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'
                            : phase.status === 'failed'
                            ? isDarkMode ? 'bg-red-500/10' : 'bg-red-50'
                            : isDarkMode ? 'bg-zinc-800/30' : 'bg-slate-100'
                        }`}
                      >
                        {/* Phase Status Icon */}
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          phase.status === 'running' 
                            ? 'bg-red-500/20'
                            : phase.status === 'completed'
                            ? 'bg-emerald-500/20'
                            : phase.status === 'failed'
                            ? 'bg-red-500/20'
                            : isDarkMode ? 'bg-zinc-700' : 'bg-slate-200'
                        }`}>
                          {phase.status === 'running' ? (
                            <Loader2 className="w-3 h-3 animate-spin text-red-500" />
                          ) : phase.status === 'completed' ? (
                            <CheckCircle className="w-3 h-3 text-emerald-500" />
                          ) : phase.status === 'failed' ? (
                            <XCircle className="w-3 h-3 text-red-500" />
                          ) : (
                            <span className={`text-xs font-medium ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                              {idx + 1}
                            </span>
                          )}
                        </div>
                        
                        {/* Phase Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={`text-sm font-medium truncate ${
                              phase.status === 'running' 
                                ? 'text-red-500'
                                : phase.status === 'completed'
                                ? isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                                : isDarkMode ? 'text-zinc-400' : 'text-slate-600'
                            }`}>
                              {phase.name}
                            </p>
                            {phase.findings_count > 0 && (
                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                isDarkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-600'
                              }`}>
                                {phase.findings_count} findings
                              </span>
                            )}
                          </div>
                          {phase.status === 'running' && (
                            <div className="mt-1">
                              <Progress value={phase.progress || 0} className="h-1" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Phase Logs */}
                  {currentScan.phase_logs && currentScan.phase_logs.length > 0 && (
                    <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/5' : 'border-slate-200'}`}>
                      <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        Latest Activity
                      </p>
                      <div className="space-y-1 max-h-24 overflow-y-auto">
                        {currentScan.phase_logs.slice(-5).reverse().map((log, idx) => (
                          <p key={idx} className={`text-xs font-mono ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                            <span className={isDarkMode ? 'text-zinc-600' : 'text-slate-400'}>
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </span>
                            {' '}{log.message}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Findings Summary */}
              {currentScan.summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
                  {Object.entries(currentScan.summary.severity_breakdown || {}).map(([severity, count]) => (
                    <div 
                      key={severity}
                      className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}
                    >
                      <div className={`w-3 h-3 rounded-full mx-auto mb-1 ${SEVERITY_COLORS[severity] || 'bg-gray-500'}`} />
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{count}</p>
                      <p className={`text-xs capitalize ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{severity}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {currentScan.status === 'completed' && (
                <Button
                  onClick={() => viewScanDetails(currentScan)}
                  className="w-full mt-4 bg-indigo-600 hover:bg-indigo-500"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Full Report
                </Button>
              )}
            </div>
          ) : (
            <div className={`rounded-xl border p-12 text-center ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <Activity className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
              <p className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>No Active Scan</p>
              <p className={`text-sm mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Start a new scan to see results here
              </p>
            </div>
          )}
        </TabsContent>
        
        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <div className={`rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            {scanHistory.length > 0 ? (
              <div className="divide-y divide-white/10">
                {scanHistory.map((scan, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 flex items-center justify-between cursor-pointer transition-colors ${
                      isDarkMode ? 'hover:bg-zinc-800/50' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => viewScanDetails(scan)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${
                        scan.status === 'completed' ? 'bg-emerald-500/20' : 
                        scan.status === 'failed' ? 'bg-red-500/20' : 'bg-yellow-500/20'
                      }`}>
                        {scan.status === 'completed' ? (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        ) : scan.status === 'failed' ? (
                          <XCircle className="w-4 h-4 text-red-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-yellow-500" />
                        )}
                      </div>
                      <div>
                        <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {scan.assessment_name || scan.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <p className={`text-xs font-mono ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {scan.target}
                        </p>
                        {scan.tags && scan.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {scan.tags.map((tag, i) => (
                              <span key={i} className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-600'}`}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {scan.summary && (
                        <div className="flex items-center gap-2">
                          {scan.summary.severity_breakdown?.critical > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-500">
                              {scan.summary.severity_breakdown.critical} Critical
                            </span>
                          )}
                          {scan.summary.severity_breakdown?.high > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-orange-500/20 text-orange-500">
                              {scan.summary.severity_breakdown.high} High
                            </span>
                          )}
                        </div>
                      )}
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {scan.started_at?.split('T')[0]}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          retestScan(scan);
                        }}
                        className={`text-xs ${isDarkMode ? 'text-indigo-400 hover:bg-indigo-500/10' : 'text-indigo-600 hover:bg-indigo-50'}`}
                        data-testid={`retest-raptor-${idx}`}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Retest
                      </Button>
                      <ChevronRight className={`w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Clock className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                <p className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>No Scan History</p>
                <p className={`text-sm mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Completed scans will appear here
                </p>
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* Report Details Tab */}
        <TabsContent value="details" className="space-y-4">
          {selectedScan ? (
            <div className="space-y-4">
              {/* Summary Card */}
              <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Scan Report
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="export-html-btn"
                      onClick={async () => {
                        try {
                          toast.info('Generating HTML report...');
                          const response = await api.get(`/raptor/scan/${selectedScan.id}/export?format=html`, {
                            responseType: 'text'
                          });
                          const blob = new Blob([response.data], { type: 'text/html' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `secureprobe-report-${selectedScan.id}.html`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                          toast.success('HTML report downloaded');
                        } catch (error) {
                          console.error('Export error:', error);
                          toast.error('Failed to export report');
                        }
                      }}
                      className={isDarkMode ? 'border-white/20 hover:bg-white/10' : ''}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      HTML
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="export-csv-btn"
                      onClick={async () => {
                        try {
                          toast.info('Generating CSV report...');
                          const response = await api.get(`/raptor/scan/${selectedScan.id}/export?format=csv`, {
                            responseType: 'blob'
                          });
                          const blob = new Blob([response.data], { type: 'text/csv' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `secureprobe-report-${selectedScan.id}.csv`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                          toast.success('CSV report downloaded');
                        } catch (error) {
                          console.error('Export error:', error);
                          toast.error('Failed to export CSV report');
                        }
                      }}
                      className={isDarkMode ? 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10' : 'border-emerald-300 text-emerald-600 hover:bg-emerald-50'}
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      CSV
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="export-pdf-btn"
                      onClick={async () => {
                        try {
                          toast.info('Opening report for PDF export...');
                          const response = await api.get(`/raptor/scan/${selectedScan.id}/export?format=pdf`, {
                            responseType: 'text'
                          });
                          const newWindow = window.open('', '_blank');
                          newWindow.document.write(response.data);
                          newWindow.document.close();
                          toast.info('Use Ctrl+P / Cmd+P to save as PDF');
                        } catch (error) {
                          console.error('Export error:', error);
                          toast.error('Failed to generate PDF view');
                        }
                      }}
                      className={isDarkMode ? 'border-white/20 hover:bg-white/10' : ''}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      selectedScan.summary?.risk_level === 'Critical' ? 'bg-red-500/20 text-red-500' :
                      selectedScan.summary?.risk_level === 'High' ? 'bg-orange-500/20 text-orange-500' :
                      selectedScan.summary?.risk_level === 'Medium' ? 'bg-yellow-500/20 text-yellow-500' :
                      'bg-emerald-500/20 text-emerald-500'
                    }`}>
                      {selectedScan.summary?.risk_level || 'Unknown'} Risk
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Assessment Name</p>
                    <p className={`font-medium text-sm mt-1 truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {selectedScan.assessment_name || 'N/A'}
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Target</p>
                    <p className={`font-mono text-sm mt-1 truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {selectedScan.target}
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Scan Type</p>
                    <p className={`font-medium text-sm mt-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {selectedScan.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Total Findings</p>
                    <p className={`font-bold text-2xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {selectedScan.summary?.total_findings || 0}
                    </p>
                  </div>
                </div>
                
                {/* Tags Display */}
                {selectedScan.tags && selectedScan.tags.length > 0 && (
                  <div className="mb-6">
                    <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedScan.tags.map((tag, i) => (
                        <span key={i} className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-700'}`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* AI Analysis */}
              {selectedScan.ai_analysis?.analysis && (
                <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-violet-950/30 border-violet-500/30' : 'bg-violet-50 border-violet-200'}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <Brain className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                    <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-violet-400' : 'text-violet-700'}`}>
                      AI-Powered Analysis
                    </h3>
                  </div>
                  <pre className={`whitespace-pre-wrap text-sm leading-relaxed ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                    {selectedScan.ai_analysis.analysis}
                  </pre>
                </div>
              )}
              
              {/* Findings List - Sorted by Severity */}
              {selectedScan.findings?.length > 0 && (
                <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
                  {/* Sub-tabs for Findings and False Positives */}
                  <div className="flex items-center gap-4 mb-4 border-b border-white/10 pb-4">
                    <button
                      onClick={() => setReportSubTab('findings')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        reportSubTab === 'findings'
                          ? isDarkMode ? 'bg-red-600/20 text-red-400 border border-red-500/30' : 'bg-red-100 text-red-700 border border-red-200'
                          : isDarkMode ? 'text-zinc-400 hover:text-white hover:bg-white/5' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                      data-testid="report-findings-tab"
                    >
                      <Bug className="w-4 h-4" />
                      Findings ({selectedScan.findings.filter(f => !f.is_false_positive).length})
                    </button>
                    <button
                      onClick={() => setReportSubTab('false_positives')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        reportSubTab === 'false_positives'
                          ? isDarkMode ? 'bg-yellow-600/20 text-yellow-400 border border-yellow-500/30' : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                          : isDarkMode ? 'text-zinc-400 hover:text-white hover:bg-white/5' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                      data-testid="report-false-positives-tab"
                    >
                      <XCircle className="w-4 h-4" />
                      False Positives ({selectedScan.findings.filter(f => f.is_false_positive).length})
                    </button>
                  </div>
                  
                  {/* Active Findings List */}
                  {reportSubTab === 'findings' && (
                    <>
                      <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Active Vulnerabilities
                      </h3>
                      <div className="space-y-4">
                        {[...selectedScan.findings]
                          .filter(f => !f.is_false_positive)
                          .sort((a, b) => {
                            const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
                            return (severityOrder[a.severity?.toLowerCase()] ?? 5) - (severityOrder[b.severity?.toLowerCase()] ?? 5);
                          }).map((finding, idx) => {
                            const originalIndex = selectedScan.findings.findIndex(f => f === finding);
                            return (
                              <div 
                                key={idx}
                                className={`p-5 rounded-lg border ${isDarkMode ? 'bg-zinc-800/50 border-white/5' : 'bg-slate-50 border-slate-200'}`}
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-start gap-3">
                                    <div className={`w-3 h-3 rounded-full mt-1.5 ${SEVERITY_COLORS[finding.severity?.toLowerCase()] || 'bg-gray-500'}`} />
                                    <div className="flex-1">
                                      <p className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                        {finding.title}
                                      </p>
                                      <div className="flex flex-wrap items-center gap-2 mt-2">
                                        {finding.cwe && (
                                          <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                                            {finding.cwe}
                                          </span>
                                        )}
                                        {finding.cvss && (
                                          <span className={`text-xs px-2 py-0.5 rounded ${
                                            parseFloat(finding.cvss) >= 9.0 ? 'bg-red-500/20 text-red-400' :
                                            parseFloat(finding.cvss) >= 7.0 ? 'bg-orange-500/20 text-orange-400' :
                                            parseFloat(finding.cvss) >= 4.0 ? 'bg-yellow-500/20 text-yellow-400' :
                                            isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-600'
                                          }`}>
                                            CVSS: {finding.cvss}
                                          </span>
                                        )}
                                        {finding.owasp && (
                                          <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-700'}`}>
                                            {finding.owasp}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => markAsFalsePositive(originalIndex)}
                                      className={`text-xs ${isDarkMode ? 'text-yellow-400 hover:bg-yellow-500/10' : 'text-yellow-600 hover:bg-yellow-50'}`}
                                      data-testid={`mark-false-positive-${idx}`}
                                    >
                                      <XCircle className="w-3 h-3 mr-1" />
                                      False Positive
                                    </Button>
                                    <span className={`text-xs px-3 py-1 rounded-full uppercase font-bold ${
                                      finding.severity?.toLowerCase() === 'critical' ? 'bg-red-500/20 text-red-500' :
                                      finding.severity?.toLowerCase() === 'high' ? 'bg-orange-500/20 text-orange-500' :
                                      finding.severity?.toLowerCase() === 'medium' ? 'bg-yellow-500/20 text-yellow-500' :
                                      finding.severity?.toLowerCase() === 'low' ? 'bg-blue-500/20 text-blue-500' :
                                      'bg-slate-500/20 text-slate-500'
                                    }`}>
                                      {finding.severity}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Description */}
                                <div className={`mb-4 ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                                  <p className="text-sm leading-relaxed">{finding.description}</p>
                                </div>
                                
                                {/* Evidence/Details Section */}
                                {(finding.evidence || finding.affected_endpoint || finding.affected_parameter || finding.request || finding.response) && (
                                  <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-zinc-900/50 border border-white/5' : 'bg-slate-100 border border-slate-200'}`}>
                                    <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                                      Technical Details
                                    </p>
                                    {finding.affected_endpoint && (
                                      <div className="mb-2">
                                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Affected Endpoint: </span>
                                        <code className={`text-xs font-mono ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                                          {finding.affected_endpoint}
                                        </code>
                                      </div>
                                    )}
                                    {finding.affected_parameter && (
                                      <div className="mb-2">
                                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Vulnerable Parameter: </span>
                                        <code className={`text-xs font-mono ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                          {finding.affected_parameter}
                                        </code>
                                      </div>
                                    )}
                                    {finding.evidence && (
                                      <div className="mb-2">
                                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Evidence: </span>
                                        <pre className={`text-xs font-mono mt-1 p-2 rounded overflow-x-auto ${isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-white text-slate-700'}`}>
                                          {typeof finding.evidence === 'object' ? JSON.stringify(finding.evidence, null, 2) : finding.evidence}
                                        </pre>
                                      </div>
                                    )}
                                    {finding.request && (
                                      <details className="mb-2">
                                        <summary className={`text-xs cursor-pointer ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>View Request</summary>
                                        <pre className={`text-xs font-mono mt-1 p-2 rounded overflow-x-auto max-h-32 ${isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-white text-slate-700'}`}>
                                          {finding.request}
                                        </pre>
                                      </details>
                                    )}
                                    {finding.response && (
                                      <details>
                                        <summary className={`text-xs cursor-pointer ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>View Response</summary>
                                        <pre className={`text-xs font-mono mt-1 p-2 rounded overflow-x-auto max-h-32 ${isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-white text-slate-700'}`}>
                                          {finding.response}
                                        </pre>
                                      </details>
                                    )}
                                  </div>
                                )}
                                
                                {/* Impact Section */}
                                {finding.impact && (
                                  <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-red-950/30 border border-red-500/20' : 'bg-red-50 border border-red-100'}`}>
                                    <p className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                                      Impact
                                    </p>
                                    <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>
                                      {finding.impact}
                                    </p>
                                  </div>
                                )}
                                
                                {/* Remediation Section */}
                                {finding.remediation && (
                                  <div className={`mb-3 p-3 rounded-lg ${isDarkMode ? 'bg-emerald-950/30 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-100'}`}>
                                    <p className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                                      Remediation Guidance
                                    </p>
                                    <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
                                      {finding.remediation}
                                    </p>
                                    {finding.remediation_steps && Array.isArray(finding.remediation_steps) && (
                                      <ol className={`mt-2 ml-4 list-decimal text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
                                        {finding.remediation_steps.map((step, stepIdx) => (
                                          <li key={stepIdx} className="mb-1">{step}</li>
                                        ))}
                                      </ol>
                                    )}
                                  </div>
                                )}
                                
                                {/* References Section */}
                                {finding.references && finding.references.length > 0 && (
                                  <div className={`pt-3 border-t ${isDarkMode ? 'border-white/5' : 'border-slate-200'}`}>
                                    <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                                      References
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      {finding.references.map((ref, refIdx) => (
                                        <a 
                                          key={refIdx}
                                          href={ref}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-zinc-700 text-indigo-400 hover:bg-zinc-600' : 'bg-slate-200 text-indigo-600 hover:bg-slate-300'}`}
                                        >
                                          <ExternalLink className="w-3 h-3" />
                                          {ref.length > 40 ? ref.substring(0, 40) + '...' : ref}
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        {selectedScan.findings.filter(f => !f.is_false_positive).length === 0 && (
                          <div className="p-8 text-center">
                            <CheckCircle className={`w-10 h-10 mx-auto mb-3 ${isDarkMode ? 'text-emerald-500' : 'text-emerald-600'}`} />
                            <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                              All findings have been marked as false positives
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  
                  {/* False Positives List */}
                  {reportSubTab === 'false_positives' && (
                    <>
                      <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        False Positives
                      </h3>
                      <div className="space-y-4">
                        {selectedScan.findings.filter(f => f.is_false_positive).length > 0 ? (
                          selectedScan.findings
                            .filter(f => f.is_false_positive)
                            .map((finding, idx) => (
                              <div 
                                key={idx}
                                className={`p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-800/30 border-white/5' : 'bg-slate-50 border-slate-200'} opacity-75`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <XCircle className={`w-4 h-4 ${isDarkMode ? 'text-yellow-500' : 'text-yellow-600'}`} />
                                    <div>
                                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                        {finding.title}
                                      </p>
                                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                                        {finding.severity} • Marked on {finding.false_positive_marked_at?.split('T')[0] || 'N/A'}
                                      </p>
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => restoreFromFalsePositive(idx)}
                                    className={`text-xs ${isDarkMode ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-emerald-600 hover:bg-emerald-50'}`}
                                    data-testid={`restore-finding-${idx}`}
                                  >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Restore
                                  </Button>
                                </div>
                              </div>
                            ))
                        ) : (
                          <div className="p-8 text-center">
                            <CheckCircle className={`w-10 h-10 mx-auto mb-3 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                            <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                              No false positives marked
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className={`rounded-xl border p-12 text-center ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <FileText className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
              <p className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>No Report Selected</p>
              <p className={`text-sm mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Select a scan from history to view its report
              </p>
            </div>
          )}
        </TabsContent>
        
        {/* Playbooks Tab */}
        <TabsContent value="playbooks" className="space-y-4">
          <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Attack Playbooks
                </h3>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Pre-built security testing workflows inspired by PentestAgent
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { id: 'owasp_api_top10', name: 'OWASP API Top 10', desc: 'Comprehensive API security assessment based on OWASP API Security Top 10 (2023)', icon: Zap, color: 'emerald', scanType: 'api_security', badge: 'NEW' },
                { id: 'web_recon', name: 'Web Reconnaissance', desc: 'Technology fingerprinting, directory enumeration, subdomain discovery', icon: Globe, color: 'blue', scanType: 'web_app_scan' },
                { id: 'api_pentest', name: 'API Penetration Testing', desc: 'Auth bypass, IDOR, injection testing, rate limiting', icon: Code, color: 'purple', scanType: 'api_security' },
                { id: 'network_pentest', name: 'Network Pentest', desc: 'Host discovery, service enumeration, vulnerability scanning', icon: Wifi, color: 'green', scanType: 'network_recon' },
                { id: 'owasp_top10', name: 'OWASP Top 10', desc: 'Comprehensive OWASP Top 10 vulnerability assessment', icon: Shield, color: 'orange', scanType: 'web_app_scan' },
                { id: 'red_team_lite', name: 'Red Team Lite', desc: 'Simulated adversary attack chain with persistence', icon: Target, color: 'red', scanType: 'penetration_test' },
                { id: 'cloud_security', name: 'Cloud Security', desc: 'IAM review, storage audit, encryption verification', icon: Server, color: 'cyan', scanType: 'cloud_config' }
              ].map((playbook) => {
                const Icon = playbook.icon;
                const isSelected = selectedPlaybook === playbook.id;
                return (
                  <button
                    key={playbook.id}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedPlaybook(null);
                      } else {
                        setSelectedPlaybook(playbook.id);
                        setScanType(playbook.scanType); // Auto-set scan type
                        toast.info(`${playbook.name} playbook selected. Scan type set to ${playbook.scanType.replace(/_/g, ' ')}.`);
                      }
                    }}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      isSelected
                        ? isDarkMode 
                          ? 'bg-red-600/20 border-red-500/50 ring-2 ring-red-500/30' 
                          : 'bg-red-50 border-red-300 ring-2 ring-red-200'
                        : isDarkMode 
                          ? 'bg-zinc-800/50 border-white/10 hover:border-white/20' 
                          : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${playbook.color}-500/20`}>
                        <Icon className={`w-5 h-5 text-${playbook.color}-500`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{playbook.name}</p>
                          {playbook.badge && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-emerald-500/20 text-emerald-500 rounded">
                              {playbook.badge}
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <span className="text-xs text-red-500 font-medium">Selected</span>
                        )}
                      </div>
                    </div>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      {playbook.desc}
                    </p>
                    <p className={`text-xs mt-2 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`}>
                      Scan type: {playbook.scanType.replace(/_/g, ' ')}
                    </p>
                  </button>
                );
              })}
            </div>
            
            {selectedPlaybook && (
              <div className={`mt-6 p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-800/50 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  <strong>Playbook Selected:</strong> {selectedPlaybook.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
                <p className={`text-xs mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Go to "New Scan" tab to configure target and start the playbook-guided assessment.
                </p>
                <Button 
                  onClick={() => setActiveTab('scan')} 
                  className="mt-3 bg-red-600 hover:bg-red-700"
                  size="sm"
                >
                  <Crosshair className="w-4 h-4 mr-2" />
                  Configure Scan
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-4">
          <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Security Notes
                </h3>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Document findings, credentials, and artifacts during penetration testing
                </p>
              </div>
              <Button 
                onClick={async () => {
                  const title = prompt('Note title:');
                  if (!title) return;
                  const content = prompt('Note content:');
                  const category = prompt('Category (credential/vulnerability/finding/artifact):', 'finding');
                  try {
                    await api.post('/raptor/notes', { title, content, category: category || 'finding' });
                    toast.success('Note created');
                    // Refresh notes
                    const response = await api.get('/raptor/notes');
                    setInteractiveNotes(response.data.notes || []);
                  } catch (error) {
                    toast.error('Failed to create note');
                  }
                }}
                className="bg-red-600 hover:bg-red-700"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Note
              </Button>
            </div>
            
            {/* Category Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { id: 'all', label: 'All Notes', color: 'slate' },
                { id: 'credential', label: 'Credentials', color: 'red' },
                { id: 'vulnerability', label: 'Vulnerabilities', color: 'orange' },
                { id: 'finding', label: 'Findings', color: 'yellow' },
                { id: 'artifact', label: 'Artifacts', color: 'blue' },
              ].map(cat => (
                <button
                  key={cat.id}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isDarkMode 
                      ? `bg-${cat.color}-500/20 text-${cat.color}-400 hover:bg-${cat.color}-500/30` 
                      : `bg-${cat.color}-100 text-${cat.color}-700 hover:bg-${cat.color}-200`
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            
            {/* Notes List */}
            <div className="space-y-3">
              {interactiveNotes.length > 0 ? (
                interactiveNotes.map((note, idx) => (
                  <div 
                    key={note.id || idx}
                    className={`p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-800/50 border-white/5' : 'bg-slate-50 border-slate-200'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            note.category === 'credential' ? 'bg-red-500/20 text-red-500' :
                            note.category === 'vulnerability' ? 'bg-orange-500/20 text-orange-500' :
                            note.category === 'artifact' ? 'bg-blue-500/20 text-blue-500' :
                            'bg-yellow-500/20 text-yellow-500'
                          }`}>
                            {note.category}
                          </span>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {note.title}
                          </p>
                        </div>
                        <p className={`text-sm mt-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                          {note.content}
                        </p>
                        <p className={`text-xs mt-2 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`}>
                          {new Date(note.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            await api.delete(`/raptor/notes/${note.id}`);
                            toast.success('Note deleted');
                            setInteractiveNotes(prev => prev.filter(n => n.id !== note.id));
                          } catch (error) {
                            toast.error('Failed to delete note');
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Edit className={`w-10 h-10 mx-auto mb-3 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    No notes yet. Add findings during your security assessment.
                  </p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
